import React from 'react';
import { Award, BookOpen, GraduationCap, Users } from 'lucide-react';
import { IMAGES } from '../constants/images';
import { SEO } from '../components/SEO';
import { Card, CardContent } from '../components/ui/card';
import { ScrollReveal } from '../components/ScrollReveal';

export const About = () => {
  const education = [
    {
      degree: 'MBBS',
      institution: 'Seth GS Medical College & KEM Hospital, Mumbai',
      year: '2015',
      description: 'One of India\'s most respected public medical institutions'
    },
    {
      degree: 'MD Radio Diagnosis',
      institution: 'Bombay Hospital Institute of Medical Sciences, Mumbai',
      year: '2019',
      description: 'Advanced training in CT, MRI, ultrasound, and Doppler imaging'
    },
    {
      degree: 'Senior Residency',
      institution: 'Tata Memorial Hospital, Mumbai',
      year: '2019-2020',
      description: 'India\'s premier cancer centre - oncology imaging and cancer-directed interventions'
    },
    {
      degree: 'DNB Radio Diagnosis',
      institution: 'DNB Board, New Delhi',
      year: '2020',
      description: 'Diplomate of National Board examination'
    },
    {
      degree: 'FVIR (with Distinction)',
      institution: 'Seth GS Medical College & KEM Hospital, Mumbai',
      year: '2021',
      description: 'Fellowship in Vascular & Interventional Radiology - Gold standard IR credential'
    },
    {
      degree: 'CCNR',
      institution: 'Royal College of Radiologists / NHS',
      year: '2024',
      description: 'Certificate Course in Neuroradiology'
    },
    {
      degree: 'MNAMS',
      institution: 'National Academy of Medical Sciences',
      year: '2024',
      description: 'Member, National Academy of Medical Sciences'
    }
  ];

  const memberships = [
    {
      org: 'ISVIR',
      full: 'Indian Society of Vascular & Interventional Radiology',
      description: 'India\'s foremost professional body for Interventional Radiologists'
    },
    {
      org: 'CIRSE',
      full: 'Cardiovascular and Interventional Radiological Society of Europe',
      description: 'Premier European IR society'
    },
    {
      org: 'MNAMS',
      full: 'National Academy of Medical Sciences',
      description: 'India\'s highest medical academy'
    }
  ];

  return (
    <>
      <SEO 
        title="About Dr. Punit Mahajan - Interventional Radiologist in Mumbai"
        description="Dr. Punit Mahajan is a fellowship-trained Interventional Radiologist in Mumbai. MD, DNB, FVIR (with Distinction), CCNR, MNAMS. KEM Hospital, Tata Memorial & Bombay Hospital alumnus. ISVIR & CIRSE member."
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-950 to-slate-900 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 pattern-hero opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold animate-text-reveal">
              Meet Dr. Punit Pushkar Mahajan
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto animate-text-reveal" style={{ animationDelay: '300ms' }}>
              Fellowship-Trained Interventional Radiologist in Mumbai
            </p>
            <div className="flex flex-wrap justify-center gap-3 pt-4 animate-text-reveal" style={{ animationDelay: '600ms' }}>
              <span className="bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-semibold">MD</span>
              <span className="bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-semibold">DNB</span>
              <span className="bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-semibold">FVIR (Distinction)</span>
              <span className="bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-semibold">CCNR</span>
              <span className="bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-semibold">MNAMS</span>
            </div>
          </div>
        </div>
      </section>

      {/* Who is Dr. Punit */}
      <section className="py-16 bg-cream-textured relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-4 border-l-4 border-amber-400 rounded-tl-2xl"></div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-4 border-r-4 border-amber-400 rounded-br-2xl"></div>
              <img 
                src={IMAGES.doctor.formal} 
                alt="Dr. Punit Mahajan"
                className="rounded-2xl shadow-2xl w-full relative z-10"
              />
            </div>
            <div className="space-y-6">
              <div className="gold-ornament">About</div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                Who is Dr. Punit Mahajan?
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Dr. Punit Pushkar Mahajan is a fellowship-trained Interventional Radiologist in Mumbai — a specialist who treats complex vascular, liver, kidney, and gynaecological conditions without open surgery, using only imaging guidance and precision catheter-based instruments through a tiny puncture in the skin.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                His approach is built on three pillars: <span className="font-semibold text-blue-900">technical precision</span>, <span className="font-semibold text-blue-900">honest communication</span>, and <span className="font-semibold text-blue-900">genuine patient care</span>. Known for explaining every procedure in plain language, always exploring conservative options first, and recommending intervention only when it is truly the best path forward.
              </p>
              <div className="bg-slate-50 border-l-4 border-amber-600 p-6 rounded-r-lg">
                <p className="text-lg italic text-slate-700">
                  "Interventional Radiology is one of the most exciting frontiers in modern medicine. We can treat conditions that once required major surgery — through a pinhole. Every procedure I perform is guided by one question: what is truly best for this patient?"
                </p>
                <p className="text-sm text-amber-600 font-semibold mt-3">— Dr. Punit Mahajan</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Education & Training */}
      <section className="py-16 bg-cream-stripes relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <GraduationCap className="w-12 h-12 text-amber-600 mx-auto mb-4" />
            <div className="gold-ornament mb-4">Credentials</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Education & Training
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Trained at Mumbai's Most Prestigious Medical Institutions
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto space-y-6">
            {education.map((edu, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 80}>
                <Card className="border-l-4 border-amber-600 hover:shadow-lg transition-shadow bg-white">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-blue-900 mb-2">{edu.degree}</h3>
                        <p className="text-slate-700 font-medium mb-1">{edu.institution}</p>
                        <p className="text-slate-600">{edu.description}</p>
                      </div>
                      <div className="mt-4 md:mt-0 md:ml-6">
                        <span className="inline-block bg-amber-100 text-amber-800 px-4 py-2 rounded-full font-semibold">
                          {edu.year}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Academic Career */}
      <section className="py-16 bg-cream-pattern relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block">
                <Users className="w-12 h-12 text-amber-600" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                Academic & Teaching Career at KEM Hospital
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Following his FVIR, Dr. Punit served as an <span className="font-semibold text-blue-900">Assistant Professor in Interventional Radiology</span> at Seth GS Medical College & KEM Hospital, Mumbai for two years.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Award className="w-6 h-6 text-amber-600 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-slate-600">Independently performed and supervised complex endovascular procedures at KEM Hospital</span>
                </li>
                <li className="flex items-start">
                  <Award className="w-6 h-6 text-amber-600 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-slate-600">Trained postgraduate radiology residents in IR techniques and image interpretation</span>
                </li>
                <li className="flex items-start">
                  <Award className="w-6 h-6 text-amber-600 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-slate-600">Contributed to departmental teaching rounds, case conferences, and CME sessions</span>
                </li>
                <li className="flex items-start">
                  <Award className="w-6 h-6 text-amber-600 mr-3 mt-1 flex-shrink-0" />
                  <span className="text-slate-600">Managed high-acuity emergency interventional cases alongside surgical and clinical teams</span>
                </li>
              </ul>
            </div>
            <div>
              <img 
                src={IMAGES.doctor.desk1} 
                alt="Dr. Punit Mahajan Teaching"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Professional Memberships */}
      <section className="py-16 bg-cream-stripes relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <BookOpen className="w-12 h-12 text-amber-600 mx-auto mb-4" />
            <div className="gold-ornament mb-4">Memberships</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Professional Memberships
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {memberships.map((member, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 150}>
                <Card className="text-center hover:shadow-lg transition-shadow border-2 hover:border-amber-600 bg-white h-full hover-lift">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 transition-transform hover:scale-110">
                      <Award className="w-8 h-8 text-amber-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-blue-900 mb-2">{member.org}</h3>
                    <p className="text-sm font-semibold text-slate-700 mb-3">{member.full}</p>
                    <p className="text-slate-600">{member.description}</p>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Research */}
      <section className="py-16 bg-gradient-to-br from-blue-900 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <BookOpen className="w-16 h-16 text-amber-500 mx-auto" />
          <h2 className="text-3xl md:text-4xl font-bold">
            Research & Publications
          </h2>
          <p className="text-xl text-slate-300">
            Dr. Punit has over <span className="text-amber-500 font-bold">15 peer-reviewed publications</span> in national and international radiology journals, covering interventional oncology, vascular disease, hepatobiliary interventions, and minimally invasive procedures.
          </p>
          <p className="text-lg text-slate-300">
            He regularly attends CME workshops and national conferences, staying at the forefront of advances in Interventional Radiology in Mumbai and beyond.
          </p>
        </div>
      </section>
    </>
  );
};
